import { z } from 'zod'
import packageJson from '../../package.json'
import localConfigJson from './local.json'
import devConfigJson from './dev.json'
import prodConfigJson from './prod.json'
import demoDevConfigJson from './demo-dev.json'
import demoProdConfigJson from './demo-prod.json'

export enum Environments {
  LOCAL = 'local',
  DEV = 'dev',
  PROD = 'prod',
  DEMO_DEV = 'demo-dev',
  DEMO_PROD = 'demo-prod',
}

export enum SardineEnvironments {
  PRODUCTION = 'production',
  SANDBOX = 'sandbox',
}

export const configSchema = z.object({
  env: z.enum([Environments.LOCAL, Environments.DEV, Environments.PROD, Environments.DEMO_DEV, Environments.DEMO_PROD]),
  appName: z.string(),
  appVariant: z.string(),
  appVersion: z.string(),
  braze: z.object({
    apiKey: z.string(),
    apiEndpoint: z.string(),
    enableLogging: z.boolean(),
  }),
  globalBaseUrl: z.string(),
  globalEnrollBaseUrl: z.string(),
  globalEnrollBaseUrlV2: z.string(),
  graphql: z.object({
    url: z.string(),
    accessToken: z.string(),
  }),
  smartystreets: z.object({
    apiKey: z.string(),
  }),
  zendeskArticles: z.object({
    formCRS: z.string(),
    advPart2A2b: z.string(),
    adv2ABAndCRS: z.string(),
    initialExternalAccountLinkingFAQ: z.string(),
    wireTransferFundsFAQ: z.string(),
    achAuthorization: z.string(),
    wireTransferAgreement: z.string(),
    helpCenter: z.string(),
    helpCenterLegal: z.string(),
    helpCenterPrivacy: z.string(),
    helpCenterFAQs: z.string(),
    investmentAdvisoryAgreement: z.string(),
    customerRelationshipAgreement: z.string(),
    privacyPolicyAdvisors: z.string(),
    depositAgreement: z.string(),
    privacyPolicySection: z.string(),
    taxCertification: z.string(),
  }),
  zendesk: z.object({
    inAppChatKey: z.string(),
  }),
  sardine: z.object({
    clientId: z.string(),
    environment: z.nativeEnum(SardineEnvironments),
    apiSubdomain: z.string(),
    pixelSubdomain: z.string(),
  }),
  fingerprintJs: z.object({
    apiKey: z.string(),
    endpoint: z.string(),
    agentDownloadPath: z.string(),
    agentGetResultPath: z.string(),
    agentBehaviorPath: z.string(),
  }),
  launchDarkly: z.object({
    clientSideId: z.string(),
  }),
  sentry: z
    .object({
      dsn: z.string(),
      environment: z.string(),
      orgSlug: z.string(),
    })
    .optional(),
  fullStory: z.object({
    debug: z.boolean(),
    devMode: z.boolean(),
    orgId: z.string(),
    host: z.string(),
    script: z.string(),
  }),
  vgs: z.object({
    vgsVaultId: z.string(),
    vgsEnv: z.string(),
    vgsCName: z.string(),
    vgsToken: z.string(),
  }),
  rudderstack: z
    .object({
      writeKey: z.string(),
      dataPlaneUrl: z.string(),
      configUrl: z.string(),
      destSDKBaseURL: z.string(),
      brazeDestinationId: z.string(),
      fullStoryDestinationId: z.string(),
    })
    .optional(),
  gtm: z
    .object({
      id: z.string(),
      baseUrl: z.string(),
      scriptParams: z.string(),
      scriptPath: z.string(),
    })
    .optional(),
  liveUpdate: z
    .object({
      remoteDomain: z.string(),
    })
    .optional(),
  incode: z.object({
    clientId: z.string(),
    sardineUrl: z.string(),
    consentId: z.string(),
  }),
  http: z
    .object({
      url: z.string(),
      accessToken: z.string(),
    })
    .optional(),
  acat: z.object({
    disclosureId: z.string(),
  }),
  aiChat: z.object({
    runtimeUrl: z.string(),
    apiUrl: z.string(),
    agent: z.string(),
  }),
})

export type Config = z.infer<typeof configSchema>

export const config = ((): Config => {
  const defaultConfig = (() => {
    switch (import.meta.env.VITE_ENV) {
      case Environments.LOCAL:
        return localConfigJson
      case Environments.DEV:
        return devConfigJson
      case Environments.PROD:
        return prodConfigJson
      case Environments.DEMO_DEV:
        return demoDevConfigJson
      case Environments.DEMO_PROD:
        return demoProdConfigJson
      default:
        return localConfigJson
    }
  })()

  const output = {
    ...defaultConfig,
    appVersion: packageJson.version,
  } as Config

  configSchema.parse(output)

  return output
})()
